/**
 * @module utils/sessionPersistence
 * @description Persists study progress to localStorage so a page refresh (or an
 * accidental tab close) RESUMES the session instead of restarting it.
 *
 * Why this matters for data integrity: without it, a refresh resets all React
 * state, so the participant redoes the access screen, consent, demographics and
 * the images they already rated — producing duplicate `starting_questionnaires`
 * rows and duplicate `after_image_questionnaires` ratings for the same image.
 *
 * The snapshot is keyed per participant so two people on the same browser never
 * inherit each other's progress, and versioned so a shape change invalidates
 * stale snapshots rather than restoring garbage.
 */

const PREFIX = "xai_study_session_v1";

/** Bump when the persisted shape changes incompatibly. */
const VERSION = 1;

function keyFor(participantKey) {
  return `${PREFIX}:${participantKey || "local"}`;
}

/** Load a saved snapshot, or null when absent/unusable. */
export function loadSession(participantKey) {
  try {
    const raw = window.localStorage.getItem(keyFor(participantKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== VERSION) return null;
    return parsed.state || null;
  } catch {
    // Private browsing / disabled storage / corrupt JSON — behave as if fresh.
    return null;
  }
}

/** Merge a partial patch into the saved snapshot. */
export function saveSession(participantKey, patch) {
  try {
    const current = loadSession(participantKey) || {};
    const next = { ...current, ...patch };
    window.localStorage.setItem(
      keyFor(participantKey),
      JSON.stringify({ version: VERSION, state: next })
    );
  } catch {
    // Never let persistence failures break the study.
  }
}

/** Remove the snapshot (called once the study is finished). */
export function clearSession(participantKey) {
  try {
    window.localStorage.removeItem(keyFor(participantKey));
  } catch {
    /* ignore */
  }
}
